import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const FALLBACK = { name: 'TELCOFAIR', logo: '' };
const BrandingContext = createContext({ ...FALLBACK, loading: true, refreshBranding: async () => undefined });
const pick = (...values) => values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

const normalizeBrand = (runtime = {}, app = {}, branding = {}, legacy = {}) => {
  const publicBranding = runtime?.branding || {};
  const legacyGeneral = legacy?.value?.general || legacy?.general || {};
  return {
    name: pick(publicBranding.appName, publicBranding.platformName, publicBranding.companyName, app.appName, app.platformName, branding.appName, branding.companyName, legacyGeneral.platformName, 'TELCOFAIR'),
    logo: pick(
      publicBranding.logoUrl, publicBranding.logo, publicBranding.platformLogoUrl, publicBranding.platformLogo,
      app.logoUrl, app.logo, app.platformLogoUrl, app.platformLogo,
      branding.logoUrl, branding.logo, branding.companyLogo,
      legacyGeneral.logoUrl, legacyGeneral.logo,
      legacy.logoUrl, legacy.logo
    ),
  };
};

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('telcofair:platform-branding') || '{}');
      return { ...FALLBACK, ...cached };
    } catch (_) {
      return FALLBACK;
    }
  });
  const [loading, setLoading] = useState(true);

  const refreshBranding = useCallback(async () => {
    const refs = [
      doc(db, 'systemSettings', 'public_runtime'),
      doc(db, 'systemSettings', 'app_settings'),
      doc(db, 'systemSettings', 'branding'),
      doc(db, 'systemConfig', 'platform'),
    ];
    try {
      const results = await Promise.allSettled(refs.map((ref) => getDoc(ref)));
      const rows = results.map((result) => result.status === 'fulfilled' && result.value.exists() ? result.value.data() || {} : {});
      const next = normalizeBrand(rows[0], rows[1], rows[2], rows[3]);
      setBrand(next);
      try { localStorage.setItem('telcofair:platform-branding', JSON.stringify(next)); } catch (error) { console.debug('Branding cache write unavailable:', error?.message || error); }
      return next;
    } catch (error) {
      console.warn('Platform branding fallback:', error?.message || error);
      return FALLBACK;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const runtimeRef = doc(db, 'systemSettings', 'public_runtime');
    const appRef = doc(db, 'systemSettings', 'app_settings');
    const brandingRef = doc(db, 'systemSettings', 'branding');
    const applySnapshot = async () => {
      if (!active) return;
      await refreshBranding();
    };
    const unsubRuntime = onSnapshot(runtimeRef, applySnapshot, (error) => console.debug('Public branding listener unavailable:', error?.message || error));
    const unsubA = onSnapshot(appRef, applySnapshot, (error) => console.debug('Branding listener unavailable:', error?.message || error));
    const unsubB = onSnapshot(brandingRef, applySnapshot, (error) => console.debug('Branding listener unavailable:', error?.message || error));
    const onBrandUpdate = () => refreshBranding();
    window.addEventListener('telcofair:platform-branding-updated', onBrandUpdate);
    refreshBranding();
    return () => {
      active = false;
      unsubRuntime?.();
      unsubA?.();
      unsubB?.();
      window.removeEventListener('telcofair:platform-branding-updated', onBrandUpdate);
    };
  }, [refreshBranding]);

  const value = useMemo(() => ({ ...brand, loading, refreshBranding }), [brand, loading, refreshBranding]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
export default BrandingContext;
